import { Box } from "@mui/material";
import React, { RefObject, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import "./scormApi";
import {
  setAssignmentId,
  setOnWarningMessage,
  setPortalId,
  setUserId,
  setUsername,
} from "./scormApi";
import { usePortal } from "../../context/PortalsContext";

// declare global {
//   interface Window {
//     API?: {
//       LMSSetValue: (key: string, value: string) => string;
//       initialized: boolean;
//       LMSCommit: () => string;
//       LMSGetValue: (key: string) => string;
//       // Add other SCORM API methods if needed
//     };
//     API_1484_11?: {
//       GetValue: (key: string) => string;
//       SetValue: (key: string, value: string) => string;
//       Commit: () => string;
//       data: {};
//       initialized: boolean;
//       // Add other SCORM 2004 API methods if needed
//     };
//   }
// }

type ScormPlayerProps = {
  assignmentId: string;
  setMaxChapter: React.Dispatch<React.SetStateAction<string>>;
  maxChapter: RefObject<string>;
  iframeRef: React.RefObject<HTMLIFrameElement | null>;
  assignmentUrl: string;
};

const ScormPlayer = ({
  assignmentId,
  setMaxChapter,
  maxChapter,
  iframeRef,
  assignmentUrl,
}: ScormPlayerProps) => {
  const { auth } = useAuth();
  const { portals } = usePortal();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [isCorruptedCourse, setIsCorruptedCourse] = useState(false);
  const redirectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const showToastRef = useRef(showToast);
  const navigateRef = useRef(navigate);
  showToastRef.current = showToast;
  navigateRef.current = navigate;

  useEffect(() => {
    let originalSetValue: ((key: string, value: string) => string) | undefined;
    let originalLMSSetValue:
      | ((key: string, value: string) => string)
      | undefined;

    // Setup wrapper for SCORM 2004
    if (window.API_1484_11) {
      originalSetValue = window.API_1484_11.SetValue;

      const modifiedSetValue = (key: string, value: string) => {
        const result = originalSetValue!(key, value);
        if (key === "cmi.location") {
          const progressValue = value.split(",")[0];
          const currentMax = parseInt(maxChapter.current || "0", 10);
          const newProgress = parseInt(progressValue, 10);
          if (newProgress > currentMax) {
            setMaxChapter(progressValue);
          }
        }

        return result;
      };

      window.API_1484_11.SetValue = modifiedSetValue;
    }

    // Setup wrapper for SCORM 1.2
    if (window.API) {
      originalLMSSetValue = window.API.LMSSetValue;

      const modifiedLMSSetValue = (key: string, value: string) => {
        const result = originalLMSSetValue!(key, value);
        if (key === "cmi.core.lesson_location") {
          const progressValue = value.split(",")[0];
          const currentMax = parseInt(maxChapter.current || "0", 10);
          const newProgress = parseInt(progressValue, 10);
          if (newProgress > currentMax) {
            setMaxChapter(progressValue);
          }
        }

        return result;
      };

      window.API.LMSSetValue = modifiedLMSSetValue;
    }

    return () => {
      // Restore original functions on cleanup
      if (window.API_1484_11 && originalSetValue) {
        window.API_1484_11.SetValue = originalSetValue;
      }
      if (window.API && originalLMSSetValue) {
        window.API.LMSSetValue = originalLMSSetValue;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Depend on primitive values only so context object reference changes don't re-run this every render (avoids update loop)
  const userId = auth.user?.UserPortalUid;
  const portalUid = portals.selectedPortal?.portalUid;
  const userDisplayName =
    auth.user != null ? `${auth.user.firstName} ${auth.user.lastName}` : "";
  useEffect(() => {
    if (userId && portalUid) {
      setUserId(userId);
      setUsername(userDisplayName);
      setPortalId(portalUid);
      setAssignmentId(assignmentId);
    }
  }, [userId, portalUid, assignmentId, userDisplayName]);

  useEffect(() => {
    setOnWarningMessage((message) => showToast(message, "warning"));
    return () => setOnWarningMessage(null);
  }, [showToast]);

  useEffect(() => {
    setIsCorruptedCourse(false);
    if (!assignmentUrl) {
      return () => {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        if (redirectTimeoutRef.current) {
          clearTimeout(redirectTimeoutRef.current);
          redirectTimeoutRef.current = null;
        }
      };
    }

    const POLL_INTERVAL_MS = 200;
    const POLL_MAX_MS = 5000;
    let elapsed = 0;

    const checkFor404 = (): boolean => {
      try {
        const iframe = iframeRef.current;
        if (!iframe?.contentWindow) return false;
        const win = iframe.contentWindow;
        const pathname = win.location?.pathname ?? "";
        if (
          pathname === "/error/404" ||
          pathname.endsWith("/error/404") ||
          pathname === "/error/401" ||
          pathname.endsWith("/error/401") ||
          pathname === "/error/403" ||
          pathname.endsWith("/error/403")
        ) {
          return true;
        }
        const doc = iframe.contentDocument;
        const bodyText = doc?.body?.innerText ?? "";
        if (
          bodyText.includes("Page not found") ||
          bodyText.includes("Access denied")
        ) {
          return true;
        }
        return false;
      } catch {
        return false;
      }
    };

    pollIntervalRef.current = setInterval(() => {
      elapsed += POLL_INTERVAL_MS;
      if (checkFor404()) {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        setIsCorruptedCourse(true);
        showToastRef.current("Empty or corrupted course", "error");
        redirectTimeoutRef.current = setTimeout(() => {
          navigateRef.current(-1);
        }, 3000);
      } else if (elapsed >= POLL_MAX_MS) {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
      }
    }, POLL_INTERVAL_MS);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
        redirectTimeoutRef.current = null;
      }
    };
    // Only re-run when assignmentUrl changes; iframeRef is stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentUrl]);

  const IFRAME_HEIGHT = 1000;

  if (isCorruptedCourse) {
    return (
      <Box
        sx={{
          width: "100%",
          minWidth: 0,
          position: "relative",
          height: IFRAME_HEIGHT,
          overflow: "hidden",
        }}
      />
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        minWidth: 0,
        position: "relative",
        height: IFRAME_HEIGHT,
        overflow: "hidden",
      }}
    >
      {assignmentUrl && (
        <Box
          component="iframe"
          src={`/${assignmentUrl}`}
          ref={iframeRef as React.RefObject<HTMLIFrameElement>}
          key="scorm-assignment"
          width="100%"
          sx={{
            border: "none",
            display: "block",
            height: IFRAME_HEIGHT,
            overflow: "hidden",
          }}
          title="SCORM Assignment"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals allow-top-navigation"
          referrerPolicy="no-referrer"
        />
      )}
    </Box>
  );
};

export default ScormPlayer;
